import React, { useState, useEffect, useCallback } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaUserTie,
  FaPhone,
  FaEnvelope,
  FaBullhorn,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaFileImport,
} from "react-icons/fa";
import apiRequest from "../services/api";

const getClientCategory = (client) => {
  if (client?.government === true) return "GOVERNMENT";
  if (client?.business === true) return "BUSINESS";
  return "INDIVIDUAL";
};

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [selectedType, setSelectedType] = useState("CUSTOMER");
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState("");
  const [hasLink, setHasLink] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");

  const [formData, setFormData] = useState({
    id: null,
    tin: "",
    name: "",
    phone: "",
    email: "",
    address: "",
    status: "ACTIVE",
    type: "CUSTOMER",
    category: "INDIVIDUAL",
    business: false,
    government: false,
    withholdingAgent: false,
    taxType: "NONE",
  });

  const change = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const inputClass =
    "w-full rounded-xl px-4 py-3 text-xs border transition focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-950/50 dark:border-white/10 dark:text-white";

  const labelClass =
    "text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400";

  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        type: selectedType,
        keyword: keyword.trim(),
        page: String(page),
        size: String(size),
      });

      const resp = await apiRequest(`clients/?${params.toString()}`);
      setClients(resp.data?.content || []);
      setTotalPages(resp.data?.totalPages || 0);
      setTotalElements(resp.data?.totalElements || 0);
    } catch (err) {
      setError(
        err?.message || "Failed to synchronize client databank ledger records."
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedType, keyword, page, size]);

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "{}");
      setHasLink(!!parsed?.data?.tenant?.externalSource);
    } catch {
      setHasLink(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    // fetchClients runs automatically after state updates.
  };

  const openModal = (mode, client = null) => {
    setModalMode(mode);
    setError("");

    if (client) {
      const category = getClientCategory(client);
      const individual = category === "INDIVIDUAL";

      setFormData({
        id: client.id,
        tin: client.tin || "",
        name: client.name || "",
        phone: client.phone || "",
        email: client.email || "",
        address: client.address || "",
        status: client.status || "ACTIVE",
        type: client.type || selectedType,
        category,
        business: category === "BUSINESS",
        government: category === "GOVERNMENT",
        withholdingAgent: individual ? false : !!client.withholdingAgent,
        taxType: individual ? "NONE" : client.taxType || "VAT",
      });
    } else {
      setFormData({
        id: null,
        tin: "",
        name: "",
        phone: "",
        email: "",
        address: "",
        status: "ACTIVE",
        type: selectedType,
        category: "INDIVIDUAL",
        business: false,
        government: false,
        withholdingAgent: false,
        taxType: "NONE",
      });
    }

    setIsModalOpen(true);
  };

  const handleCategoryChange = (category) => {
    const individual = category === "INDIVIDUAL";

    setFormData((prev) => ({
      ...prev,
      category,
      business: category === "BUSINESS",
      government: category === "GOVERNMENT",
      withholdingAgent: individual ? false : prev.withholdingAgent,
      taxType: individual
        ? "NONE"
        : prev.taxType === "NONE"
        ? "VAT"
        : prev.taxType,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    const individual = formData.category === "INDIVIDUAL";

    const payload = {
      ...formData,
      business: formData.category === "BUSINESS",
      government: formData.category === "GOVERNMENT",
      withholdingAgent: individual ? false : formData.withholdingAgent,
      taxType: individual ? "NONE" : formData.taxType,
    };

    delete payload.category;

    try {
      await apiRequest("clients", {
        method: modalMode === "create" ? "POST" : "PUT",
        body: payload,
      });

      setIsModalOpen(false);
      await fetchClients();
    } catch (err) {
      setError(
        err?.message ||
          "Client Service pipeline rejected transaction schema verification."
      );
    }
  };

  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you sure you want to delete client "${name || "Guest"}"?`
      )
    )
      return;

    setError("");

    try {
      await apiRequest(`clients/${id}`, { method: "DELETE" });
      await fetchClients();
    } catch (err) {
      setError(err?.message || "Failed to delete client.");
    }
  };

  const handleImportExternal = async () => {
    setError("");
    setIsImporting(true);

    try {
      const resp = await apiRequest(
        `clients/import-external?type=${encodeURIComponent(selectedType)}`,
        { method: "POST" }
      );

      const count = resp?.data ?? 0;
      window.alert(
        `Successfully imported ${count} ${selectedType.toLowerCase()}(s).`
      );

      await fetchClients();
    } catch (err) {
      setError(
        err?.message || "Failed to import clients from external source."
      );
    } finally {
      setIsImporting(false);
    }
  };

  const categoryBadge = (client) => {
    const category = getClientCategory(client);

    const styles = {
      GOVERNMENT:
        "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
      BUSINESS:
        "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
      INDIVIDUAL:
        "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700/30 dark:text-slate-400 dark:border-slate-600/20",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold border ${styles[category]}`}
      >
        {category}
      </span>
    );
  };

  const booleanBadge = (value) => (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold border ${
        value
          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
          : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700/30 dark:text-slate-400 dark:border-slate-600/20"
      }`}
    >
      {value ? "Yes" : "No"}
    </span>
  );

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-6 pb-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-5 rounded-2xl shadow-sm border bg-white border-slate-200 dark:bg-slate-900/40 dark:border-white/10">
          <div className="flex items-center gap-3.5">
            <div className="p-3 rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-400">
              <FaUserTie className="text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                Client Directory
              </h1>
              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                Register and manage customers and suppliers
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <FaFilter className="text-slate-400 text-xs" />
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setPage(0);
                }}
                className={inputClass}
              >
                <option value="CUSTOMER">CUSTOMER</option>
                <option value="SUPPLIER">SUPPLIER</option>
              </select>
            </div>

            {hasLink && (
              <button
                type="button"
                onClick={handleImportExternal}
                disabled={isImporting}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30 disabled:opacity-60"
              >
                <FaFileImport className={isImporting ? "animate-pulse" : ""} />
                {isImporting ? "Importing..." : `Import ${selectedType}s`}
              </button>
            )}

            <button
              type="button"
              onClick={() => openModal("create")}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl"
            >
              <FaPlus /> New Client
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 p-3 rounded-xl border bg-white border-slate-200 dark:bg-slate-900/20 dark:border-white/5">
          <form onSubmit={handleSearchSubmit} className="flex-1 flex gap-2">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search by name, TIN, phone..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className={`${inputClass} pl-9`}
              />
              <FaSearch className="absolute left-3 top-3 text-xs text-slate-400" />
            </div>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg border text-xs font-medium bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
            >
              Search
            </button>
          </form>
        </div>

        {error && (
          <div className="p-4 rounded-xl text-xs font-medium border bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Client table */}
        <div className="border rounded-2xl shadow-sm overflow-hidden bg-white border-slate-200 dark:bg-slate-900/30 dark:border-white/10">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1100px]">
              <thead>
                <tr className="border-b text-[10px] font-bold tracking-wider uppercase bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                  <th className="px-6 py-4">TIN</th>
                  <th className="px-6 py-4">Full Name</th>
                  <th className="px-6 py-4">Contact</th>
                  <th className="px-6 py-4">Address</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Tax Type</th>
                  <th className="px-6 py-4">Withholding Agent</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y text-xs divide-slate-100 text-slate-600 dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-12 text-slate-500"
                    >
                      Fetching data...
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-12 text-slate-500"
                    >
                      No {selectedType.toLowerCase()} found.
                    </td>
                  </tr>
                ) : (
                  clients.map((c) => (
                    <tr
                      key={c.id}
                      className="group transition hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-6 py-4">{c.tin || "—"}</td>
                      <td className="px-6 py-4 font-semibold">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                            {c.name || "Guest"}
                          </span>
                          <span className="text-[9px] mt-0.5 text-emerald-700 dark:text-emerald-400 font-mono uppercase">
                            {c.type || "CUSTOMER"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <span className="flex items-center gap-1.5">
                            <FaPhone className="text-[10px] text-slate-400" />
                            {c.phone || "—"}
                          </span>
                          {c.email && (
                            <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                              <FaEnvelope className="text-[10px]" />
                              {c.email}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">{c.address || "—"}</td>
                      <td className="px-6 py-4">{categoryBadge(c)}</td>
                      <td className="px-6 py-4">
                        {getClientCategory(c) === "INDIVIDUAL"
                          ? "—"
                          : c.taxType || "—"}
                      </td>
                      <td className="px-6 py-4">
                        {getClientCategory(c) === "INDIVIDUAL"
                          ? "—"
                          : booleanBadge(!!c.withholdingAgent)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${
                            c.status === "ACTIVE"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/5 dark:text-emerald-400 dark:border-emerald-500/20"
                              : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/5 dark:text-amber-400 dark:border-amber-500/20"
                          }`}
                        >
                          {c.status || "ACTIVE"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            title="View"
                            onClick={() => openModal("view", c)}
                            className="p-2 rounded-lg border bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => openModal("edit", c)}
                            className="p-2 rounded-lg border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => handleDelete(c.id, c.name)}
                            className="p-2 rounded-lg border bg-red-50 border-red-200 text-red-600 hover:bg-red-100 dark:bg-red-600/10 dark:border-red-500/20 dark:text-red-400"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between text-xs bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-400">
              <div>
                Showing{" "}
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                  {clients.length}
                </span>{" "}
                of{" "}
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                  {totalElements}
                </span>{" "}
                records.
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  className="p-2 rounded-md border bg-white border-slate-200 disabled:opacity-30 dark:bg-slate-800 dark:border-slate-700"
                >
                  <FaChevronLeft />
                </button>
                <span className="font-mono">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((prev) => prev + 1)}
                  className="p-2 rounded-md border bg-white border-slate-200 disabled:opacity-30 dark:bg-slate-800 dark:border-slate-700"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create / Edit / View modal */}
        <div
          className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
            isModalOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="absolute inset-0 bg-slate-900/40 dark:bg-slate-950/60 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          />

          <div
            className={`relative w-full max-w-3xl mx-auto border rounded-t-3xl p-6 sm:p-8 shadow-2xl transition-transform duration-300 space-y-6 overflow-y-auto max-h-[90vh] bg-white border-slate-200 dark:bg-slate-900 dark:border-white/10 ${
              isModalOpen ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                  {modalMode} Client
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {modalMode === "create"
                    ? `Creating a new ${selectedType.toLowerCase()}`
                    : modalMode === "edit"
                    ? "Update client details"
                    : "View client details"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl border bg-slate-100 border-slate-200 text-slate-600 dark:bg-white/5 dark:border-white/5 dark:text-slate-300"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Customer / Supplier */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>Record Type</label>
                  <select
                    disabled={modalMode === "view"}
                    value={formData.type}
                    onChange={(e) => change("type", e.target.value)}
                    className={inputClass}
                  >
                    <option value="CUSTOMER">CUSTOMER</option>
                    <option value="SUPPLIER">SUPPLIER</option>
                  </select>
                </div>

                {/* Status */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>Status</label>
                  <select
                    disabled={modalMode === "view"}
                    value={formData.status}
                    onChange={(e) => change("status", e.target.value)}
                    className={inputClass}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>

                {/* Client category */}
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label className={labelClass}>Client Category</label>
                  <select
                    value={formData.category}
                    disabled={modalMode === "view"}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className={inputClass}
                  >
                    <option value="GOVERNMENT">Government</option>
                    <option value="BUSINESS">Business</option>
                    <option value="INDIVIDUAL">Individual</option>
                  </select>
                </div>

                {/* Name */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>Client Name</label>
                  <input
                    type="text"
                    placeholder='Leave blank to default to "Guest"'
                    disabled={modalMode === "view"}
                    value={formData.name}
                    onChange={(e) => change("name", e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* TIN */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>TIN Number</label>
                  <input
                    type="text"
                    placeholder="e.g. 000234"
                    disabled={modalMode === "view"}
                    value={formData.tin}
                    onChange={(e) => change("tin", e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* Phone */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    <FaPhone className="inline mr-1" />
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. +251..."
                    disabled={modalMode === "view"}
                    value={formData.phone}
                    onChange={(e) => change("phone", e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>Email Address</label>
                  <input
                    type="email"
                    placeholder="e.g. tracking@domain.com"
                    disabled={modalMode === "view"}
                    value={formData.email}
                    onChange={(e) => change("email", e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* Address */}
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label className={labelClass}>Address</label>
                  <input
                    type="text"
                    placeholder="e.g. Bole, AA"
                    disabled={modalMode === "view"}
                    value={formData.address}
                    onChange={(e) => change("address", e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* Tax fields for Business / Government */}
                {formData.category !== "INDIVIDUAL" && (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className={labelClass}>Tax Type</label>
                      <select
                        value={formData.taxType}
                        onChange={(e) => change("taxType", e.target.value)}
                        disabled={modalMode === "view"}
                        className={inputClass}
                      >
                        <option value="VAT">VAT</option>
                        <option value="TOT">TOT</option>
                        <option value="NONE">Exempt / Non-registered</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="clientWithholdingAgent"
                        checked={formData.withholdingAgent}
                        onChange={(e) =>
                          change("withholdingAgent", e.target.checked)
                        }
                        disabled={modalMode === "view"}
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-slate-600 dark:bg-slate-800"
                      />
                      <label
                        htmlFor="clientWithholdingAgent"
                        className="text-sm text-slate-700 dark:text-slate-300"
                      >
                        Withholding Agent
                      </label>
                    </div>
                  </>
                )}

                {/* Footer */}
                <div className="sm:col-span-2 flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                  >
                    Close
                  </button>

                  {modalMode !== "view" && (
                    <button
                      type="submit"
                      className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold"
                    >
                      {modalMode === "create"
                        ? "Save Client"
                        : "Apply Modifications"}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientManagement;
